import type { RbacMembership } from '../../services/rbacAdminApi';
import type { SafetyPortfolioResponse, SafetyPortfolioSiteCoverage } from '../../services/reportsApi';
import {
  formatPortfolioDataStateLabel,
  formatPortfolioStateDetail,
} from './workspacePortfolioSiteComparison';

function normalize(value?: string | null): string {
  return String(value || '').trim();
}

const PARTIAL_REASON_LABELS: Record<string, string> = {
  permission_denied: 'One or more sites are permission denied for your account',
  outside_requested_org: 'One or more requested sites are outside this organization',
  site_load_failed: 'One or more sites failed to load portfolio data',
  unavailable: 'One or more sites are temporarily unavailable',
};

export interface PortfolioSiteAccessRow {
  siteId: string;
  displayName: string;
  accessState: string;
  accessStateLabel: string;
  reasonDetail: string | null;
  includedInPortfolio: boolean;
}

export interface PortfolioGovernanceSummary {
  orgId: string;
  isPartial: boolean;
  partialReasons: string[];
  authorizedCount: number;
  loadedCount: number;
  deniedCount: number;
  unavailableCount: number;
  siteAccessRows: PortfolioSiteAccessRow[];
}

export interface OrgMembershipScopeSummary {
  scopeKey: string;
  siteLabel: string;
  membershipCount: number;
  roles: string[];
}

export function formatGovernancePartialReason(reason?: string | null): string {
  const key = normalize(reason).toLowerCase();
  if (!key) return 'Portfolio scope is partial';
  return PARTIAL_REASON_LABELS[key] || key.replace(/_/g, ' ');
}

function siteLabelFromCoverage(siteId: string, siteCoverage?: SafetyPortfolioSiteCoverage[]): string {
  const match = (siteCoverage || []).find((site) => normalize(site.site_id) === siteId);
  return normalize(match?.display_name) || siteId || 'Site';
}

function buildSiteAccessRow(site: SafetyPortfolioSiteCoverage): PortfolioSiteAccessRow {
  const siteId = normalize(site.site_id);
  const accessState = normalize(site.data_state).toLowerCase() || 'unknown';
  return {
    siteId,
    displayName: normalize(site.display_name) || siteId || 'Authorized site',
    accessState,
    accessStateLabel: formatPortfolioDataStateLabel(accessState),
    reasonDetail: formatPortfolioStateDetail(site),
    includedInPortfolio: Boolean(site.included) && accessState === 'loaded',
  };
}

export function buildPortfolioGovernanceSummary(
  portfolio: SafetyPortfolioResponse,
): PortfolioGovernanceSummary {
  const siteCoverage = portfolio.site_coverage || [];
  const siteAccessRows = siteCoverage.map(buildSiteAccessRow);
  const authorizedCount = siteAccessRows.filter((row) => row.accessState !== 'permission_denied').length;
  const loadedCount = siteAccessRows.filter((row) => row.includedInPortfolio).length;
  const deniedCount = siteAccessRows.filter((row) => row.accessState === 'permission_denied').length;
  const unavailableCount = siteAccessRows.filter((row) => row.accessState === 'unavailable').length;
  const scopeReasons = (portfolio.scope?.partial_reasons || []).map(formatGovernancePartialReason);
  const permissionReasons = portfolio.permissions?.partial
    ? ['Your account does not have portfolio access to every site in this organization']
    : [];
  const partialReasons = Array.from(new Set([...scopeReasons, ...permissionReasons].filter(Boolean)));

  return {
    orgId: normalize(portfolio.org_id || portfolio.scope?.org_id),
    isPartial: Boolean(portfolio.scope?.partial || portfolio.permissions?.partial),
    partialReasons,
    authorizedCount,
    loadedCount,
    deniedCount,
    unavailableCount,
    siteAccessRows,
  };
}

export function buildDeniedSiteGovernanceRows(
  portfolio: SafetyPortfolioResponse,
): PortfolioSiteAccessRow[] {
  const siteCoverage = portfolio.site_coverage || [];
  const deniedFromCoverage = siteCoverage
    .filter((site) => normalize(site.data_state).toLowerCase() === 'permission_denied')
    .map(buildSiteAccessRow);
  if (deniedFromCoverage.length > 0) return deniedFromCoverage;

  return (portfolio.permissions?.denied_sites || []).map((entry) => {
    const siteId = normalize(entry.site_id);
    const reason = normalize(entry.reason).toLowerCase() || 'permission_denied';
    return {
      siteId,
      displayName: siteLabelFromCoverage(siteId, siteCoverage),
      accessState: 'permission_denied',
      accessStateLabel: formatPortfolioDataStateLabel('permission_denied'),
      reasonDetail: reason,
      includedInPortfolio: false,
    };
  });
}

function formatMembershipRoleLabel(role: string): string {
  const normalized = normalize(role);
  if (!normalized) return 'Unknown role';
  return normalized
    .split('_')
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(' ');
}

export function buildOrgMembershipScopeSummaries(
  memberships: RbacMembership[],
  siteCoverage?: SafetyPortfolioSiteCoverage[],
): OrgMembershipScopeSummary[] {
  const siteLabels = new Map<string, string>();
  (siteCoverage || []).forEach((site) => {
    const siteId = normalize(site.site_id);
    if (!siteId) return;
    siteLabels.set(siteId, normalize(site.display_name) || siteId);
  });

  const grouped = new Map<string, { roles: Set<string>; count: number }>();
  memberships.forEach((membership) => {
    const siteId = normalize(membership.site_id);
    const scopeKey = siteId || '__org_wide__';
    const current = grouped.get(scopeKey) || { roles: new Set<string>(), count: 0 };
    current.count += 1;
    const role = normalize(membership.role);
    if (role) current.roles.add(formatMembershipRoleLabel(role));
    grouped.set(scopeKey, current);
  });

  const summaries: OrgMembershipScopeSummary[] = Array.from(grouped.entries()).map(([scopeKey, value]) => {
    if (scopeKey === '__org_wide__') {
      return {
        scopeKey,
        siteLabel: 'Org-wide access',
        membershipCount: value.count,
        roles: Array.from(value.roles).sort((left, right) => left.localeCompare(right)),
      };
    }
    return {
      scopeKey,
      siteLabel: siteLabels.get(scopeKey) || scopeKey,
      membershipCount: value.count,
      roles: Array.from(value.roles).sort((left, right) => left.localeCompare(right)),
    };
  });

  summaries.sort((left, right) => {
    if (left.scopeKey === '__org_wide__') return -1;
    if (right.scopeKey === '__org_wide__') return 1;
    return left.siteLabel.localeCompare(right.siteLabel, undefined, { sensitivity: 'base' });
  });
  return summaries;
}

export function portfolioGovernanceEmptyMessage(summary: PortfolioGovernanceSummary | null): string | null {
  if (!summary) return 'Portfolio governance status is unavailable until the org portfolio loads.';
  if (summary.siteAccessRows.length > 0) return null;
  return 'No site access rows were returned for this org portfolio. Check organization scope and RBAC membership.';
}
